import { decodeBase64Url } from "./base64";
import { ExtractedLink, GmailMessage, GmailMessageHeader, GmailMessageListItem, ParsedMessageMetadata } from "./types";
import { extractLinksFromPayload } from "./linkExtractor";

const GMAIL_API = "https://gmail.googleapis.com/gmail/v1/users/me";

async function gmailRequest<T>(token: string, path: string): Promise<T> {
  const res = await fetch(`${GMAIL_API}${path}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail API error: ${res.status} ${text}`);
  }
  return res.json();
}

interface ListMessagesOptions {
  maxResults?: number;
  pageToken?: string;
  q?: string;
}

export async function listMessages(
  token: string,
  { maxResults = 20, pageToken, q }: ListMessagesOptions = {}
): Promise<{ messages: GmailMessageListItem[]; nextPageToken?: string }> {
  const params = new URLSearchParams();
  params.set("maxResults", String(maxResults));
  if (pageToken) params.set("pageToken", pageToken);
  if (q) params.set("q", q);

  const data = await gmailRequest<{ messages?: GmailMessageListItem[]; nextPageToken?: string }>(token, `/messages?${params.toString()}`);
  return { messages: data.messages ?? [], nextPageToken: data.nextPageToken };
}

export async function getMessageMetadata(token: string, id: string): Promise<ParsedMessageMetadata> {
  const msg = await gmailRequest<GmailMessage>(token, `/messages/${id}?format=metadata&metadataHeaders=From&metadataHeaders=Subject&metadataHeaders=Reply-To&metadataHeaders=Date`);
  const headers = normalizeHeaders(msg.payload?.headers ?? msg.headers ?? []);
  return {
    id: msg.id,
    subject: headers.subject,
    from: headers.from,
    replyTo: headers["reply-to"],
    date: headers.date,
    snippet: msg.snippet,
    headers,
    payload: msg.payload
  };
}

export async function getMessageDetail(token: string, id: string): Promise<{
  metadata: ParsedMessageMetadata;
  links: ExtractedLink[];
  rawPayload?: string;
}> {
  const msg = await gmailRequest<GmailMessage>(token, `/messages/${id}?format=full`);
  const headers = normalizeHeaders(msg.payload?.headers ?? msg.headers ?? []);
  const metadata: ParsedMessageMetadata = {
    id: msg.id,
    subject: headers.subject,
    from: headers.from,
    replyTo: headers["reply-to"],
    date: headers.date,
    snippet: msg.snippet,
    headers,
    payload: msg.payload
  };
  const links = extractLinksFromPayload(msg.payload);
  return { metadata, links };
}

export function normalizeHeaders(headers: GmailMessageHeader[]): Record<string, string> {
  return headers.reduce<Record<string, string>>((acc, header) => {
    acc[header.name.toLowerCase()] = header.value;
    return acc;
  }, {});
}

export function extractBodyText(payload?: GmailMessage["payload"]): string {
  if (!payload) return "";
  if (payload?.mimeType === "text/plain" && payload.body?.data) {
    return decodeBase64Url(payload.body.data);
  }
  const parts = payload.parts ?? [];
  for (const part of parts) {
    if (part.mimeType === "text/plain" && part.body?.data) {
      return decodeBase64Url(part.body.data);
    }
    if (part.parts?.length) {
      const nested = extractBodyText(part);
      if (nested) return nested;
    }
  }
  return "";
}
